import { useState, memo, useRef, useEffect, useContext } from 'react';
import { TextArea, ExpandableText, Image } from '@/sub-luca-plugin/compatible-components';
import { update_luca_ask_record } from '@/sub-luca-plugin/apis/qa';
import { QaRecordsContext } from '../../helper';
import { useLockFn, previewPicture, getClientRectInfo, isWeApp } from '@/sub-luca-plugin/utils';
import { OcrEditSvg } from '@/sub-luca-plugin/compatible-components/svg-icons';
import { scrollIntoViewCallback } from './helper';
import { IQaRecordItem } from '../../index';
import classNames from 'classnames';
import './index.scss';

/**
 * OCR识别内容
 */
export interface IOCR {
    recordId: string;
    picture: string;
    content: string;
    hideOcrContentEditBtn?: boolean;
}

const Ocr = ({ recordId, content, picture, hideOcrContentEditBtn }: IOCR) => {
    const { getQaStoreByContext } = useContext(QaRecordsContext);
    const hasPlaceCursorBehind = useRef(false);
    const initFocused = useRef(false);
    const headerRef = useRef<HTMLDivElement | null>(null);
    // const textAreaWidth = useRef(0);
    const [value, setValue] = useState<string>(content);
    const [isEdit, setIsEdit] = useState(false);

    const handleCancelEdit = () => {
        initFocused.current = false;
        hasPlaceCursorBehind.current = false;
        setIsEdit(false);
        setValue(content);
    };

    const handleEdit = () => {
        // 流输出中禁止编辑
        if (!getQaStoreByContext()?.validateCanSend()) return;
        setIsEdit(true);
        scrollIntoViewCallback('#questioner-ocr-scroll-tag');
    };

    const handleSubmit = useLockFn(async () => {
        // 没有内容或流输出中禁止提交
        if (!getQaStoreByContext()?.validateCanSend()) return;
        const { status } = await update_luca_ask_record({
            recordId: recordId!,
            ocrContent: { ocrContent: value, editState: true },
        });
        if (status === 0) {
            getQaStoreByContext()?.setQaRecords((oldList: IQaRecordItem[]) =>
                oldList.map(item => {
                    if (item.recordId !== recordId) return item;
                    return { ...item, content: value! };
                })
            );
        }
        hasPlaceCursorBehind.current = false;
        setIsEdit(false);
    });

    useEffect(() => {
        // 当状态变为不可编辑时，关闭打开的编辑弹窗
        if (hideOcrContentEditBtn) setIsEdit(false);
    }, [hideOcrContentEditBtn]);

    // useEffect(() => {
    //     getClientRectInfo('.expandable-text-container').then(res => {
    //         // 获取ExpandableText组件的宽度，设置多行文本输入框的宽度
    //         textAreaWidth.current = res.clientWidth - 22;
    //     });
    // }, []);

    return (
        <div className="questioner-container">
            <div className="questioner-ocr">
                <div className="questioner-ocr__combine-picture" onClick={() => previewPicture(picture)}>
                    <div className="questioner-ocr__combine-picture-mark">原图</div>
                    <Image className="questioner-ocr__img" src={picture} mode="aspectFill" />
                </div>
                {!!content && (
                    <div className="questioner-ocr__info">
                        <div className="questioner-ocr__info-header" ref={headerRef}>
                            <div className="questioner-ocr__info-title">识别内容</div>
                            {!hideOcrContentEditBtn && !isEdit && <OcrEditSvg className="questioner-ocr__info-edit" onClick={handleEdit} />}
                        </div>
                        {isEdit ? (
                            <>
                                <TextArea
                                    className="questioner-ocr__textarea"
                                    autoSize
                                    autoFocus={!isWeApp}
                                    value={value}
                                    placeholder="请输入识别内容"
                                    adjustPosition={false}
                                    idOfAdjustPositionByScroll="ocr-text-area"
                                    maxLength={5000}
                                    onChange={(ev: any) => setValue(ev.target.value)}
                                    ref={(node: any) => {
                                        if (node?.focus && !initFocused.current && isWeApp) {
                                            // 小程序中，编辑状态下追问，会导致输入框频繁聚焦，所以这里申明变量阻止一下
                                            initFocused.current = true;
                                            setTimeout(node.focus, 500);
                                        } else if (node && !hasPlaceCursorBehind.current) {
                                            hasPlaceCursorBehind.current = true;
                                            node?.resizableTextArea?.textArea.setSelectionRange(content.length, content.length);
                                        }
                                    }}
                                />
                                <div className="questioner-ocr__hide-text">{content}</div>
                                <div className="questioner-ocr__operations">
                                    <div
                                        className="questioner-ocr__cancel"
                                        onClick={handleCancelEdit}
                                        ref={node => setTimeout(() => node?.scrollIntoView?.({ block: 'end' }), 0)}
                                    >
                                        取消
                                    </div>
                                    <div
                                        className={classNames('questioner-ocr__submit', value === '' && 'questioner-ocr__submit--disabled')}
                                        onClick={value === '' ? undefined : handleSubmit}
                                    >
                                        提交
                                    </div>
                                </div>
                            </>
                        ) : (
                            <ExpandableText content={value} lineHeight={22} maxLine={10} />
                        )}
                    </div>
                )}
                <div id="questioner-ocr-scroll-tag" />
            </div>
        </div>
    );
};

export default memo(Ocr);
